import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './cfFtpInfo.api';
import { getSelectedCfFtpInfo } from './cfFtpInfo.selectors';
import {
  GET_CF_FTP_INFO_LIST,
  GET_CF_FTP_INFO,
  CREATE_CF_FTP_INFO,
  UPDATE_CF_FTP_INFO,
  DELETE_CF_FTP_INFO,
  GO_TO_CF_FTP_INFO_LIST,
  GO_TO_CF_FTP_INFO_EDIT,
  GO_TO_CF_FTP_INFO_VIEW,
  GO_TO_CF_FTP_INFO_CREATE,
  SELECT_CF_FTP_INFO,
  DeleteCfFtpInfoThunkAction,
  UpdateCfFtpInfoThunkAction,
  CreateCfFtpInfoThunkAction,
  SET_CF_FTP_INFO_LOADING,
  SetCfFtpInfoLoadingAction,
  CfFtpInfoActionTypes,
  CfFtpInfo,
  OPEN_CF_FTP_INFO_CREATE_POPUP,
  CLOSE_CF_FTP_INFO_CREATE_POPUP,
  SelectCfFtpInfoAction,
  OpenCfFtpInfoCreatePopupAction,
  CloseCfFtpInfoCreatePopupAction,
} from './cfFtpInfo.types';
import { CF_FTP_INFO_ROUTE, SYSTEM_CONFIGURATION_ROUTE, MISCELLANEOUS } from '../../constants';

export function openCfFtpInfoCreatePopup(cfFtpInfo?: CfFtpInfo): OpenCfFtpInfoCreatePopupAction {
  return {
    type: OPEN_CF_FTP_INFO_CREATE_POPUP,
    payload: { cfFtpInfo },
  };
}

export function closeCfFtpInfoCreatePopup(): CloseCfFtpInfoCreatePopupAction {
  return {
    type: CLOSE_CF_FTP_INFO_CREATE_POPUP,
  };
}

export function goToCfFtpInfoList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CF_FTP_INFO_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CF_FTP_INFO_ROUTE}`);
  };
}

export function goToCfFtpInfoCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CF_FTP_INFO_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CF_FTP_INFO_ROUTE}/create`);
  };
}

export function goToCfFtpInfoEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CF_FTP_INFO_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CF_FTP_INFO_ROUTE}/edit`);
  };
}

export function goToCfFtpInfoView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_CF_FTP_INFO_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${CF_FTP_INFO_ROUTE}/view`);
  };
}

export function setCfFtpInfoLoadingAction(
  isLoading: boolean,
): SetCfFtpInfoLoadingAction {
  return {
    type: SET_CF_FTP_INFO_LOADING,
    payload: { isLoading },
  };
}

export function getCfFtpInfoList(deliveryFunctionId: string): CfFtpInfoActionTypes {
  return {
    type: GET_CF_FTP_INFO_LIST,
    payload: api.getCfFtpInfoList(deliveryFunctionId),
  };
}

export function getCfFtpInfo(
  deliveryFunctionId: string, id: string,
): CfFtpInfoActionTypes {
  return {
    type: GET_CF_FTP_INFO,
    payload: api.getCfFtpInfo(deliveryFunctionId, id),
  };
}

export function createCfFtpInfo(
  deliveryFunctionId: string,
  body: WithAuditDetails<CfFtpInfo>,
  history: History,
): CreateCfFtpInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_CF_FTP_INFO,
        payload: api.createCfFtpInfo(deliveryFunctionId, body),
      });

      dispatch(getCfFtpInfoList(deliveryFunctionId));
      dispatch(goToCfFtpInfoList(history));
      showSuccessMessage(i18n.t('cfFtpInfo.create.success', { name: body.data?.FTPID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('cfFtpInfo.create.failed', { name: body.data?.FTPID, error: errorMessage }));
    }
  };
}

export function createCfFtpInfoFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<CfFtpInfo>,
): CreateCfFtpInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setCfFtpInfoLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_CF_FTP_INFO,
        payload: api.createCfFtpInfo(deliveryFunctionId, body),
      });

      dispatch(closeCfFtpInfoCreatePopup());
      showSuccessMessage(i18n.t('cfFtpInfo.create.success', { name: body.data?.FTPID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('cfFtpInfo.create.failed', { name: body.data?.FTPID, error: errorMessage }));

    } finally {
      dispatch(setCfFtpInfoLoadingAction(false));
      dispatch(getCfFtpInfoList(deliveryFunctionId));
    }
  };
}

export function updateCfFtpInfo(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<CfFtpInfo>,
  history: History,
): UpdateCfFtpInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_CF_FTP_INFO,
        payload: api.updateCfFtpInfo(deliveryFunctionId, id, body),
      });

      dispatch(getCfFtpInfoList(deliveryFunctionId));
      dispatch(goToCfFtpInfoList(history));
      showSuccessMessage(i18n.t('cfFtpInfo.update.success', { name: body.data?.FTPID }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('cfFtpInfo.update.failed', { name: body.data?.FTPID, error: errorMessage }));
    }
  };
}

export function deleteCfFtpInfo(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteCfFtpInfoThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedCfFtpInfo] = getSelectedCfFtpInfo(getState());

    try {
      await dispatch({
        type: DELETE_CF_FTP_INFO,
        payload: api.deleteCfFtpInfo(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('cfFtpInfo.delete.success', { name: selectedCfFtpInfo?.FTPID }), 5000);
      if (history != null) {
        dispatch(goToCfFtpInfoList(history));
      }
    } catch (e) {
      showError(i18n.t('cfFtpInfo.delete.failed', { name: selectedCfFtpInfo?.FTPID }));

    } finally {
      dispatch(getCfFtpInfoList(deliveryFunctionId));
    }
  };
}

export function selectCfFtpInfo(
  cfFtpInfo: CfFtpInfo[],
): SelectCfFtpInfoAction {
  return {
    type: SELECT_CF_FTP_INFO,
    payload: cfFtpInfo,
  };
}
